import { useEffect } from "react";

const BODY_SCROLL_LOCK_COUNT_KEY = "timeReportDetailsPanelScrollLockCount";
const BODY_PREVIOUS_OVERFLOW_KEY = "timeReportDetailsPanelPreviousOverflow";
const BODY_PREVIOUS_PADDING_RIGHT_KEY =
  "timeReportDetailsPanelPreviousPaddingRight";

/**
 * Locks background page scrolling while the time report details panel is open.
 *
 * Uses a body-level ref-count so multiple overlays can coexist without
 * unlocking scroll too early. Restores previous body overflow/padding values
 * when the last lock is released.
 */
export const useTimeReportDetailsPanelScrollLock = (
  isEnabled: boolean,
): void => {
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    const body = document.body;
    const previousLockCountRaw = body.dataset[BODY_SCROLL_LOCK_COUNT_KEY];
    const previousLockCount =
      previousLockCountRaw == null ? 0 : Number(previousLockCountRaw) || 0;
    const nextLockCount = previousLockCount + 1;
    body.dataset[BODY_SCROLL_LOCK_COUNT_KEY] = String(nextLockCount);

    if (nextLockCount === 1) {
      body.dataset[BODY_PREVIOUS_OVERFLOW_KEY] = body.style.overflow;
      body.dataset[BODY_PREVIOUS_PADDING_RIGHT_KEY] = body.style.paddingRight;
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      body.style.overflow = "hidden";
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    return () => {
      const currentLockCountRaw = body.dataset[BODY_SCROLL_LOCK_COUNT_KEY];
      const currentLockCount =
        currentLockCountRaw == null ? 0 : Number(currentLockCountRaw) || 0;
      const updatedLockCount = Math.max(0, currentLockCount - 1);

      if (updatedLockCount === 0) {
        body.style.overflow = body.dataset[BODY_PREVIOUS_OVERFLOW_KEY] ?? "";
        body.style.paddingRight =
          body.dataset[BODY_PREVIOUS_PADDING_RIGHT_KEY] ?? "";
        delete body.dataset[BODY_SCROLL_LOCK_COUNT_KEY];
        delete body.dataset[BODY_PREVIOUS_OVERFLOW_KEY];
        delete body.dataset[BODY_PREVIOUS_PADDING_RIGHT_KEY];
        return;
      }

      body.dataset[BODY_SCROLL_LOCK_COUNT_KEY] = String(updatedLockCount);
    };
  }, [isEnabled]);
};
